import React, { useState, useMemo, useEffect, useCallback } from "react";
import { exportQueryResponse } from "../services/exportService";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
} from "chart.js";
import { Bar, Line, Pie, Doughnut, Scatter } from "react-chartjs-2";
import { NoDataState } from "./NoDataState";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
);

// Premium color palette - modern, vibrant, harmonious
const CHART_COLORS = [
  "rgba(99, 102, 241, 0.85)", // Indigo
  "rgba(16, 185, 129, 0.85)", // Emerald
  "rgba(245, 158, 11, 0.85)", // Amber
  "rgba(239, 68, 68, 0.85)", // Rose
  "rgba(59, 130, 246, 0.85)", // Blue
  "rgba(168, 85, 247, 0.85)", // Purple
  "rgba(20, 184, 166, 0.85)", // Teal
  "rgba(249, 115, 22, 0.85)", // Orange
  "rgba(236, 72, 153, 0.85)", // Pink
  "rgba(34, 197, 94, 0.85)", // Green
  "rgba(6, 182, 212, 0.85)", // Cyan
  "rgba(139, 92, 246, 0.85)", // Violet
];

const CHART_BORDERS = CHART_COLORS.map((c) => c.replace("0.85)", "1)"));

const POWER_BI_CATEGORY_COLORS = [
  "rgba(17, 141, 255, 0.95)",
  "rgba(18, 35, 158, 0.95)",
  "rgba(230, 108, 55, 0.95)",
  "rgba(107, 0, 123, 0.95)",
  "rgba(118, 185, 0, 0.95)",
  "rgba(0, 153, 153, 0.95)",
];

const POWER_BI_CATEGORY_BORDERS = POWER_BI_CATEGORY_COLORS.map((c) =>
  c.replace("0.95)", "1)"),
);

// Detect dark mode
const isDarkMode = () => document.documentElement.classList.contains("dark");

// Dark-mode aware chart defaults
const getChartTheme = () => {
  const dark = isDarkMode();
  return {
    textColor: dark ? "rgba(203, 213, 225, 0.9)" : "rgba(51, 65, 85, 0.9)",
    gridColor: dark ? "rgba(51, 65, 85, 0.4)" : "rgba(226, 232, 240, 0.6)",
    tooltipBg: dark ? "rgba(15, 23, 42, 0.95)" : "rgba(255, 255, 255, 0.95)",
    tooltipText: dark ? "#e2e8f0" : "#334155",
    tooltipBorder: dark ? "rgba(51, 65, 85, 0.5)" : "rgba(203, 213, 225, 0.5)",
  };
};

// Format numbers for display (1000 → 1K, 1000000 → 1M)
const formatAxisValue = (value: number): string => {
  if (Math.abs(value) >= 1_000_000) return (value / 1_000_000).toFixed(1) + "M";
  if (Math.abs(value) >= 1_000) return (value / 1_000).toFixed(1) + "K";
  return value.toLocaleString();
};

const formatCurrencyAxisValue = (value: number): string => {
  const prefix = value < 0 ? "-R" : "R";
  return `${prefix}${formatAxisValue(Math.abs(value))}`;
};

const isGrowthCurrencyChart = (
  chartTitle?: string,
  chartWidgetId?: string,
): boolean => {
  const knownGrowthTitles = [
    "Sum of Invoiced by Province",
    "Sum of Invoiced by District",
    "Invoiced compared to Previous Invoiced",
  ];
  if (knownGrowthTitles.includes(chartTitle?.trim() || "")) {
    return true;
  }

  const normalizedWidgetId = chartWidgetId || "";
  return [
    "-invoiced-by-province",
    "-invoiced-by-district",
    "-invoiced-comparison",
  ].some((token) => normalizedWidgetId.includes(token));
};

const isScrollableGrowthChart = (
  chartTitle?: string,
  chartWidgetId?: string,
  pointCount = 0,
): boolean =>
  isGrowthCurrencyChart(chartTitle, chartWidgetId) && pointCount > 10;

const getScrollableChartWidth = (
  pointCount: number,
  pixelsPerPoint: number,
): number => Math.max(720, pointCount * pixelsPerPoint);

const parseNumericValue = (value: any): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(num) ? num : null;
};

const isGaugeMaxKey = (key: string): boolean => {
  const normalized = key.toLowerCase();
  return [
    "max",
    "maximum",
    "target",
    "goal",
    "total",
    "overall",
    "capacity",
    "limit",
  ].some(
    (token) =>
      normalized === token ||
      normalized.includes(`${token}_`) ||
      normalized.includes(`_${token}`) ||
      normalized.endsWith(token),
  );
};

const isPercentageKey = (key: string): boolean => {
  const normalized = key.toLowerCase();
  return ["percent", "percentage", "pct", "rate", "ratio"].some((token) =>
    normalized.includes(token),
  );
};

const formatGaugeValue = (value: number, showPercent: boolean): string => {
  if (showPercent) {
    return `${Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1)}%`;
  }
  return formatAxisValue(value);
};

// Client-side visualization correction (mirrors backend logic)
const correctVisualization = (data: any[], vizType: string): string => {
  if (!data || data.length === 0) return vizType;
  try {
    const headers = Object.keys(data[0] || {});
    const numericKeys = headers.filter(
      (k) => parseNumericValue(data[0][k]) !== null,
    );

    // Single aggregate → metric_card
    if (data.length === 1 && numericKeys.length === 1 && headers.length <= 2) {
      if (
        ![
          "metric_card",
          "metric",
          "gauge",
          "pie_chart_full",
          "scatter_chart",
          "map",
        ].includes(vizType)
      )
        return "metric_card";
    }
    // Pie with too many → bar
    if (["pie_chart", "pie"].includes(vizType) && data.length > 8)
      return "bar_chart";
    if (vizType === "doughnut" && data.length > 10) return "bar_chart";
    // Single-row bar/line → metric
    if (
      ["bar_chart", "bar", "line_chart", "line"].includes(vizType) &&
      data.length === 1 &&
      numericKeys.length <= 1
    )
      return "metric_card";
    // 2-col small table → bar
    if (
      vizType === "table" &&
      headers.length === 2 &&
      numericKeys.length === 1 &&
      data.length >= 2 &&
      data.length <= 15
    )
      return "bar_chart";
  } catch {
    /* ignore */
  }
  return vizType;
};

const clampValue = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

const projectMercator = (
  latitude: number,
  longitude: number,
  zoom: number,
): { x: number; y: number } => {
  const clampedLatitude = clampValue(latitude, -85.05112878, 85.05112878);
  const latitudeRadians = (clampedLatitude * Math.PI) / 180;
  const scale = 256 * Math.pow(2, zoom);
  return {
    x: ((longitude + 180) / 360) * scale,
    y:
      ((1 -
        Math.log(Math.tan(latitudeRadians) + 1 / Math.cos(latitudeRadians)) /
          Math.PI) /
        2) *
      scale,
  };
};

const formatMapTooltipValue = (value: number, decimals = 6): string => {
  return value.toFixed(decimals).replace(/\.?0+$/, "");
};

interface DataVisualizerProps {
  data: any[];
  visualizationType: string;
  title?: string;
  onAskMore?: (query: string, data?: any) => void;
  onInfo?: () => void;
  onEdit?: () => void;
  onExport?: () => void;
  isExporting?: boolean;
  exportLabel?: string;
  onDelete?: () => void;
  showKebabMenu?: boolean;
  setShowKebabMenu?: (show: boolean) => void;
  dragHandleProps?: any;
  widgetId?: string;
}

interface MapTooltipState {
  latitude: number;
  longitude: number;
  growth: number;
  branchCode: string;
  leftPercent: number;
  topPercent: number;
}

export const DataVisualizer: React.FC<DataVisualizerProps> = ({
  data,
  visualizationType,
  title,
  onAskMore,
  onInfo,
  onEdit,
  onExport,
  isExporting,
  exportLabel,
  onDelete,
  showKebabMenu,
  setShowKebabMenu,
  dragHandleProps,
  widgetId,
}) => {
  const [mapTooltip, setMapTooltip] = useState<MapTooltipState | null>(null);

  // Handle clicking outside kebab menu to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showKebabMenu && !target.closest(".kebab-menu-container-dv")) {
        setShowKebabMenu && setShowKebabMenu(false);
      }
    };

    if (showKebabMenu) {
      document.addEventListener("click", handleClickOutside, true);
      return () => {
        document.removeEventListener("click", handleClickOutside, true);
      };
    }
  }, [showKebabMenu, setShowKebabMenu]);

  const EnhancedDataTable = ({
    data,
    tableTitle,
  }: {
    data: any[];
    tableTitle?: string;
  }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [sortColumn, setSortColumn] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
      {},
    );
    const [isExportingTable, setIsExportingTable] = useState(false);
    const [exportError, setExportError] = useState<string | null>(null);

    // Separate sentinel totals rows from regular data
    const totalsRows = useMemo(
      () => data.filter((row) => row.__IS_TOTALS_ROW__),
      [data],
    );
    const regularData = useMemo(
      () => data.filter((row) => !row.__IS_TOTALS_ROW__),
      [data],
    );

    // Export data: regular rows + totals appended (sentinel field stripped)
    const exportData = useMemo(() => {
      if (totalsRows.length === 0) return regularData;
      const cleanedTotals = totalsRows.map(
        ({ __IS_TOTALS_ROW__, ...rest }) => rest,
      );
      return [...regularData, ...cleanedTotals];
    }, [regularData, totalsRows]);

    const handleTableExport = useCallback(async () => {
      if (isExportingTable) return;
      setIsExportingTable(true);
      setExportError(null);
      try {
        await exportQueryResponse(exportData, tableTitle || "Export", "table");
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : "Export failed. Please try again.";
        setExportError(msg);
      } finally {
        setIsExportingTable(false);
      }
    }, [isExportingTable, exportData, tableTitle]);

    // Filter and search logic
    const filteredData = useMemo(() => {
      if (!regularData || regularData.length === 0) return [];
      let filtered = regularData;

      // Apply search
      if (searchTerm) {
        filtered = filtered.filter((row) =>
          Object.values(row).some((value) =>
            String(value).toLowerCase().includes(searchTerm.toLowerCase()),
          ),
        );
      }

      // Apply column filters
      Object.entries(columnFilters).forEach(([column, filterValue]) => {
        if (filterValue) {
          filtered = filtered.filter((row) =>
            String(row[column])
              .toLowerCase()
              .includes(filterValue.toLowerCase()),
          );
        }
      });

      return filtered;
    }, [regularData, searchTerm, columnFilters]);

    // Sorting logic
    const sortedData = useMemo(() => {
      if (!regularData || regularData.length === 0) return [];
      if (!sortColumn) return filteredData;

      return [...filteredData].sort((a, b) => {
        const aVal = a[sortColumn];
        const bVal = b[sortColumn];

        // Handle numbers
        const aNum = Number(aVal);
        const bNum = Number(bVal);
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return sortDirection === "asc" ? aNum - bNum : bNum - aNum;
        }

        // Handle strings
        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();
        if (sortDirection === "asc") {
          return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
        } else {
          return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
        }
      });
    }, [filteredData, sortColumn, sortDirection]);

    // Determine display mode - show at least 10 rows
    const minDisplayRows = 10;
    const useCompactView = sortedData.length > minDisplayRows;

    // Pagination logic - show at least 10 rows per page
    const effectiveItemsPerPage = Math.max(minDisplayRows, itemsPerPage);
    const totalPages = Math.ceil(sortedData.length / effectiveItemsPerPage);
    const startIndex = (currentPage - 1) * effectiveItemsPerPage;
    const paginatedData = sortedData.slice(
      startIndex,
      startIndex + effectiveItemsPerPage,
    );

    const handleSort = (column: string) => {
      if (sortColumn === column) {
        setSortDirection(sortDirection === "asc" ? "desc" : "asc");
      } else {
        setSortColumn(column);
        setSortDirection("asc");
      }
    };

    const handleColumnFilter = (column: string, value: string) => {
      setColumnFilters((prev) => ({
        ...prev,
        [column]: value,
      }));
      setCurrentPage(1);
    };

    // Early return check after all hooks
    if (!regularData || regularData.length === 0) {
      return (
        <NoDataState
          type="data"
          message="No records found"
          showSuggestions={false}
          showExtras={false}
        />
      );
    }

    const headers = Object.keys(regularData[0]);

    return (
      <div className="w-full">
        {/* Search and Controls */}
        <div className="mb-4 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="text"
              placeholder="Search all columns..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-transparent text-gray-900 dark:text-gray-100"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                Clear
              </button>
            )}
            <button
              onClick={handleTableExport}
              disabled={isExportingTable}
              className="flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-700 dark:hover:bg-emerald-900/40 disabled:opacity-50 transition-colors"
              title="Export to Excel"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              {isExportingTable ? "Exporting…" : "Export"}
            </button>
            {exportError && (
              <span className="text-xs text-red-600 dark:text-red-400 max-w-[200px] leading-tight">
                {exportError}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <span>Show:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-transparent text-gray-900 dark:text-gray-100"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>entries</span>
          </div>
        </div>

        {/* Results info */}
        <div className="mb-2 text-sm text-gray-600 dark:text-gray-400">
          {useCompactView ? (
            <>
              Showing {startIndex + 1} to{" "}
              {Math.min(startIndex + effectiveItemsPerPage, sortedData.length)}{" "}
              of {sortedData.length} entries (compact view)
            </>
          ) : (
            <>
              Showing {startIndex + 1} to{" "}
              {Math.min(startIndex + effectiveItemsPerPage, sortedData.length)}{" "}
              of {sortedData.length} entries
            </>
          )}
          {filteredData.length !== regularData.length &&
            ` (filtered from ${regularData.length} total entries)`}
        </div>

        {/* Table */}
        <div className="overflow-x-auto w-full border border-gray-200 dark:border-gray-700 rounded-lg">
          <table className="min-w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50/30 dark:bg-gray-700/30 dark:text-gray-400">
              <tr>
                {headers.map((header) => (
                  <th key={header} className="px-4 py-3 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => handleSort(header)}
                        className="flex items-center gap-1 hover:text-gray-900 dark:hover:text-gray-100"
                      >
                        <span>{header.replace(/_/g, " ").toUpperCase()}</span>
                        <span className="text-xs">
                          {sortColumn === header
                            ? sortDirection === "asc"
                              ? "↑"
                              : "↓"
                            : "↕"}
                        </span>
                      </button>
                      <input
                        type="text"
                        placeholder={`Filter ${header}...`}
                        value={columnFilters[header] || ""}
                        onChange={(e) =>
                          handleColumnFilter(header, e.target.value)
                        }
                        className="px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 rounded bg-transparent text-gray-900 dark:text-gray-100"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((row, i) => (
                <tr
                  key={i}
                  className="border-b dark:border-gray-700 hover:bg-gray-50/50 dark:hover:bg-gray-600/20"
                >
                  {headers.map((header) => (
                    <td key={header} className="px-4 py-2 whitespace-nowrap">
                      {row[header] !== null && row[header] !== undefined
                        ? String(row[header])
                        : "N/A"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            {totalsRows.length > 0 && (
              <tfoot>
                {totalsRows.map((row, i) => (
                  <tr
                    key={i}
                    className="border-t-2 border-gray-400 dark:border-gray-500 bg-gray-100 dark:bg-gray-800 font-semibold text-gray-900 dark:text-gray-100"
                  >
                    {headers.map((header) => (
                      <td key={header} className="px-4 py-2 whitespace-nowrap">
                        {row[header] !== null &&
                        row[header] !== undefined &&
                        row[header] !== ""
                          ? String(row[header])
                          : ""}
                      </td>
                    ))}
                  </tr>
                ))}
              </tfoot>
            )}
          </table>
        </div>

        {/* Pagination */}
        {(totalPages > 1 || useCompactView) && (
          <div className="mt-4 flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Previous
              </button>

              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded ${
                        currentPage === pageNum
                          ? "bg-blue-500 text-white border-blue-500"
                          : "hover:bg-gray-50 dark:hover:bg-gray-700"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Last
              </button>
            </div>

            <div className="text-sm text-gray-600 dark:text-gray-400">
              Page {currentPage} of {totalPages}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDataTable = (data: any[]) => {
    return <EnhancedDataTable data={data} tableTitle={title} />;
  };

  const renderBarChart = (data: any[]) => {
    if (!data || data.length === 0) {
      return (
        <NoDataState
          type="data"
          message="No data available for visualization"
          showSuggestions={false}
          showExtras={false}
        />
      );
    }

    const theme = getChartTheme();
    const headers = Object.keys(data[0]);
    const numericKeys = headers.filter(
      (k) => typeof data[0][k] === "number" || !isNaN(Number(data[0][k])),
    );
    const useCurrencyAxis = isGrowthCurrencyChart(title, widgetId);
    const axisFormatter = useCurrencyAxis
      ? formatCurrencyAxisValue
      : formatAxisValue;

    // Single record with multiple numeric fields → separate bars
    if (data.length === 1 && numericKeys.length > 1) {
      const chartData = {
        labels: numericKeys.map((key) => key.replace(/_/g, " ").toUpperCase()),
        datasets: [
          {
            label: "Values",
            data: numericKeys.map((key) => Number(data[0][key]) || 0),
            backgroundColor: CHART_COLORS.slice(0, numericKeys.length),
            borderColor: CHART_BORDERS.slice(0, numericKeys.length),
            borderWidth: 1,
            borderRadius: 6,
            borderSkipped: false as const,
          },
        ],
      };
      const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          title: {
            display: !!title,
            text: title || "Data Comparison",
            color: theme.textColor,
            font: { size: 14, weight: "bold" as const },
          },
          tooltip: {
            backgroundColor: theme.tooltipBg,
            titleColor: theme.tooltipText,
            bodyColor: theme.tooltipText,
            borderColor: theme.tooltipBorder,
            borderWidth: 1,
            cornerRadius: 8,
            padding: 10,
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: { color: theme.gridColor },
            ticks: {
              color: theme.textColor,
              callback: (v: any) => axisFormatter(Number(v)),
            },
          },
          x: {
            grid: { display: false },
            ticks: { color: theme.textColor, maxRotation: 45 },
          },
        },
      };
      return (
        <div className="h-full w-full">
          <Bar data={chartData} options={options} />
        </div>
      );
    }

    // Multiple records - support multi-series if multiple numeric columns
    const labelKey =
      headers.find((k) => !numericKeys.includes(k)) || headers[0];
    const isInvoicesCapturedByEmployee =
      labelKey === "Employee" &&
      numericKeys.length === 1 &&
      numericKeys[0] === "Invoices Captured";
    const categoryColors = isInvoicesCapturedByEmployee
      ? POWER_BI_CATEGORY_COLORS
      : CHART_COLORS;
    const categoryBorders = isInvoicesCapturedByEmployee
      ? POWER_BI_CATEGORY_BORDERS
      : CHART_BORDERS;
    const datasets =
      numericKeys.length > 1
        ? numericKeys.map((key, i) => ({
            label: key.replace(/_/g, " ").toUpperCase(),
            data: data.map((d) => Number(d[key]) || 0),
            backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
            borderColor: CHART_BORDERS[i % CHART_BORDERS.length],
            borderWidth: 1,
            borderRadius: 6,
            borderSkipped: false as const,
          }))
        : [
            {
              label: (numericKeys[0] || headers[1] || "")
                .replace(/_/g, " ")
                .toUpperCase(),
              data: data.map(
                (d) => Number(d[numericKeys[0] || headers[1]]) || 0,
              ),
              backgroundColor: data.map(
                (_, index) => categoryColors[index % categoryColors.length],
              ),
              borderColor: data.map(
                (_, index) => categoryBorders[index % categoryBorders.length],
              ),
              borderWidth: 1,
              borderRadius: isInvoicesCapturedByEmployee ? 2 : 6,
              borderSkipped: false as const,
            },
          ];

    const chartData = {
      labels: data.map((d) => String(d[labelKey])),
      datasets,
    };
    const isScrollableGrowth = isScrollableGrowthChart(
      title,
      widgetId,
      data.length,
    );
    const scrollableWidth = getScrollableChartWidth(
      data.length,
      numericKeys.length > 1 ? 44 : 54,
    );
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: isInvoicesCapturedByEmployee
          ? {
              position: "right" as const,
              align: "start" as const,
              onClick: () => undefined,
              labels: {
                color: theme.textColor,
                usePointStyle: true,
                pointStyle: "circle" as const,
                boxWidth: 8,
                boxHeight: 8,
                padding: 14,
                font: { size: 12 },
                generateLabels: () =>
                  data.map((row, index) => ({
                    text: String(row[labelKey] ?? ""),
                    fillStyle: categoryColors[index % categoryColors.length],
                    strokeStyle:
                      categoryBorders[index % categoryBorders.length],
                    lineWidth: 0,
                    pointStyle: "circle" as const,
                    hidden: false,
                    datasetIndex: 0,
                    index,
                  })),
              },
            }
          : {
              position: "top" as const,
              labels: {
                color: theme.textColor,
                usePointStyle: true,
                padding: 16,
                font: { size: 11 },
              },
            },
        title: {
          display: !!title,
          text:
            title ||
            `${(numericKeys[0] || "").replace(/_/g, " ").toUpperCase()} by ${labelKey.replace(/_/g, " ").toUpperCase()}`,
          color: theme.textColor,
          font: { size: 14, weight: "bold" as const },
        },
        tooltip: {
          backgroundColor: theme.tooltipBg,
          titleColor: theme.tooltipText,
          bodyColor: theme.tooltipText,
          borderColor: theme.tooltipBorder,
          borderWidth: 1,
          cornerRadius: 8,
          padding: 10,
          callbacks: useCurrencyAxis
            ? {
                label: (item: any) =>
                  `${item.dataset.label}: ${formatCurrencyAxisValue(Number(item.raw) || 0)}`,
              }
            : undefined,
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: { color: theme.gridColor },
          title: {
            display: isInvoicesCapturedByEmployee,
            text: "Invoices Captured",
            color: theme.textColor,
            font: { size: 12, weight: "bold" as const },
          },
          ticks: {
            color: theme.textColor,
            callback: (v: any) => axisFormatter(Number(v)),
          },
        },
        x: {
          grid: { display: false },
          title: {
            display: isInvoicesCapturedByEmployee,
            text: "Employee",
            color: theme.textColor,
            font: { size: 12, weight: "bold" as const },
          },
          ticks: {
            autoSkip: !isScrollableGrowth,
            color: theme.textColor,
            maxRotation: isInvoicesCapturedByEmployee
              ? 0
              : isScrollableGrowth
                ? 70
                : 45,
            minRotation: isScrollableGrowth ? 55 : 0,
          },
        },
      },
    };
    if (isScrollableGrowth) {
      return (
        <div className="h-full w-full overflow-x-auto overflow-y-hidden">
          <div className="h-full" style={{ width: `${scrollableWidth}px` }}>
            <Bar data={chartData} options={options} />
          </div>
        </div>
      );
    }
    return (
      <div className="h-full w-full">
        <Bar data={chartData} options={options} />
      </div>
    );
  };

  const renderLineChart = (data: any[]) => {
    if (!data || data.length === 0) {
      return (
        <NoDataState
          type="data"
          message="No trend data available"
          showSuggestions={false}
          showExtras={false}
        />
      );
    }
    const theme = getChartTheme();
    const headers = Object.keys(data[0]);
    const numericKeys = headers.filter((key) =>
      data.some((row) => parseNumericValue(row[key]) !== null),
    );
    const labelKey =
      headers.find((key) => !numericKeys.includes(key)) || headers[0];
    const valueKeys = numericKeys.length ? numericKeys : headers.slice(1, 2);
    const useCurrencyAxis = isGrowthCurrencyChart(title, widgetId);
    const axisFormatter = useCurrencyAxis
      ? formatCurrencyAxisValue
      : formatAxisValue;
    const isScrollableGrowth = isScrollableGrowthChart(
      title,
      widgetId,
      data.length,
    );
    const scrollableWidth = getScrollableChartWidth(data.length, 42);
    const chartData = {
      labels: data.map((d) => String(d[labelKey])),
      datasets: valueKeys.map((valueKey, index) => ({
        label: valueKey.replace(/_/g, " ").toUpperCase(),
        data: data.map((d) => parseNumericValue(d[valueKey]) || 0),
        borderColor: CHART_BORDERS[index % CHART_BORDERS.length],
        backgroundColor: CHART_COLORS[index % CHART_COLORS.length].replace(
          "0.85)",
          "0.12)",
        ),
        fill: valueKeys.length === 1,
        tension: 0.35,
        pointBackgroundColor: CHART_BORDERS[index % CHART_BORDERS.length],
        pointBorderColor: isDarkMode() ? "#1e293b" : "#ffffff",
        pointBorderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 6,
      })),
    };
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top" as const,
          labels: { color: theme.textColor, usePointStyle: true, padding: 16 },
        },
        title: {
          display: !!title,
          text:
            title || `${valueKeys[0].replace(/_/g, " ").toUpperCase()} Trend`,
          color: theme.textColor,
          font: { size: 14, weight: "bold" as const },
        },
        tooltip: {
          backgroundColor: theme.tooltipBg,
          titleColor: theme.tooltipText,
          bodyColor: theme.tooltipText,
          borderColor: theme.tooltipBorder,
          borderWidth: 1,
          cornerRadius: 8,
          padding: 10,
          callbacks: useCurrencyAxis
            ? {
                label: (item: any) =>
                  `${item.dataset.label}: ${formatCurrencyAxisValue(Number(item.raw) || 0)}`,
              }
            : undefined,
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: { color: theme.gridColor },
          ticks: {
            color: theme.textColor,
            callback: (v: any) => axisFormatter(Number(v)),
          },
        },
        x: {
          grid: { display: false },
          ticks: {
            autoSkip: !isScrollableGrowth,
            color: theme.textColor,
            maxRotation: isScrollableGrowth ? 70 : 50,
            minRotation: isScrollableGrowth ? 55 : 0,
          },
        },
      },
    };
    if (isScrollableGrowth) {
      return (
        <div className="h-full w-full overflow-x-auto overflow-y-hidden">
          <div className="h-full" style={{ width: `${scrollableWidth}px` }}>
            <Line data={chartData} options={options} />
          </div>
        </div>
      );
    }
    return (
      <div className="h-full w-full">
        <Line data={chartData} options={options} />
      </div>
    );
  };

  // Combo chart: bars for first numeric series (left Y, currency), lines for the rest (right Y, %)
  const renderComboChart = (data: any[]) => {
    if (!data || data.length === 0) {
      return (
        <NoDataState
          type="data"
          message="No trend data available"
          showSuggestions={false}
          showExtras={false}
        />
      );
    }
    const theme = getChartTheme();
    const headers = Object.keys(data[0]);
    const numericKeys = headers.filter((key) =>
      data.some((row) => parseNumericValue(row[key]) !== null),
    );
    const labelKey =
      headers.find((key) => !numericKeys.includes(key)) || headers[0];
    const labels = data.map((d) => String(d[labelKey]));

    const barKey = numericKeys[0];
    const lineKeys = numericKeys.slice(1);

    const lineColors = [
      "rgba(99, 102, 241, 1)", // Indigo – RTTO
      "rgba(18, 35, 158, 1)", // Dark navy – R&UTTO
      "rgba(245, 158, 11, 1)", // Amber – TCOTTO
      "rgba(168, 85, 247, 1)", // Purple – TCTTO
      "rgba(239, 68, 68, 1)", // Rose – RDTTD
    ];

    const datasets: any[] = [
      {
        type: "bar" as const,
        label: barKey,
        data: data.map((d) => parseNumericValue(d[barKey]) || 0),
        backgroundColor: "rgba(59, 130, 246, 0.75)",
        borderColor: "rgba(59, 130, 246, 1)",
        borderWidth: 1,
        borderRadius: 3,
        borderSkipped: false as const,
        yAxisID: "yLeft",
        order: 2,
      },
      ...lineKeys.map((key, i) => ({
        type: "line" as const,
        label: key,
        data: data.map((d) => parseNumericValue(d[key]) || 0),
        borderColor: lineColors[i % lineColors.length],
        backgroundColor: lineColors[i % lineColors.length].replace(
          ", 1)",
          ", 0.08)",
        ),
        tension: 0.35,
        pointBackgroundColor: lineColors[i % lineColors.length],
        pointBorderColor: isDarkMode() ? "#1e293b" : "#ffffff",
        pointBorderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 6,
        fill: false,
        yAxisID: "yRight",
        order: 1,
      })),
    ];

    const options: any = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index" as const, intersect: false },
      plugins: {
        legend: {
          position: "top" as const,
          labels: {
            color: theme.textColor,
            usePointStyle: true,
            padding: 14,
            font: { size: 11 },
            generateLabels: (chart: any) =>
              chart.data.datasets.map((ds: any, i: number) => ({
                text: ds.label,
                fillStyle:
                  ds.type === "bar" ? ds.backgroundColor : ds.borderColor,
                strokeStyle: ds.borderColor,
                lineWidth: ds.type === "bar" ? 0 : 2,
                pointStyle: ds.type === "bar" ? "rect" : "circle",
                hidden: false,
                datasetIndex: i,
              })),
          },
        },
        title: {
          display: !!title,
          text: title || "Trend",
          color: theme.textColor,
          font: { size: 14, weight: "bold" as const },
        },
        tooltip: {
          backgroundColor: theme.tooltipBg,
          titleColor: theme.tooltipText,
          bodyColor: theme.tooltipText,
          borderColor: theme.tooltipBorder,
          borderWidth: 1,
          cornerRadius: 8,
          padding: 10,
          callbacks: {
            label: (item: any) => {
              if (item.datasetIndex === 0) {
                return `${item.dataset.label}: ${formatCurrencyAxisValue(Number(item.raw) || 0)}`;
              }
              return `${item.dataset.label}: ${(Number(item.raw) || 0).toFixed(2)}%`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: theme.textColor, maxRotation: 50, autoSkip: true },
        },
        yLeft: {
          type: "linear" as const,
          position: "left" as const,
          beginAtZero: true,
          grid: { color: theme.gridColor },
          ticks: {
            color: theme.textColor,
            callback: (v: any) => formatCurrencyAxisValue(Number(v)),
          },
          title: {
            display: true,
            text: "Turnover (Ex. VAT)",
            color: theme.textColor,
            font: { size: 11 },
          },
        },
        yRight: {
          type: "linear" as const,
          position: "right" as const,
          beginAtZero: true,
          grid: { drawOnChartArea: false },
          ticks: {
            color: theme.textColor,
            callback: (v: any) => `${Number(v).toFixed(0)}%`,
          },
          title: {
            display: true,
            text: "Ratio (%)",
            color: theme.textColor,
            font: { size: 11 },
          },
        },
      },
    };

    const chartData = { labels, datasets };
    return (
      <div className="h-full w-full">
        <Bar data={chartData as any} options={options} />
      </div>
    );
  };

  const renderPieChart = (data: any[]) => {
    if (!data || data.length === 0) {
      return (
        <NoDataState
          type="data"
          message="No distribution data available"
          showSuggestions={false}
          showExtras={false}
        />
      );
    }
    const theme = getChartTheme();
    const headers = Object.keys(data[0]);
    const labelKey = headers[0];
    const valueKey =
      headers.find(
        (k) => typeof data[0][k] === "number" || !isNaN(Number(data[0][k])),
      ) || headers[1];
    const chartData = {
      labels: data.map((d) => String(d[labelKey])),
      datasets: [
        {
          data: data.map((d) => Number(d[valueKey]) || 0),
          backgroundColor: data.map(
            (_, index) => CHART_COLORS[index % CHART_COLORS.length],
          ),
          borderColor: isDarkMode()
            ? "rgba(30, 41, 59, 0.8)"
            : "rgba(255, 255, 255, 0.9)",
          borderWidth: 2,
          hoverOffset: 8,
        },
      ],
    };
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "bottom" as const,
          labels: {
            color: theme.textColor,
            usePointStyle: true,
            padding: 12,
            font: { size: 11 },
          },
        },
        title: {
          display: !!title,
          text:
            title ||
            `${valueKey.replace(/_/g, " ").toUpperCase()} Distribution`,
          color: theme.textColor,
          font: { size: 14, weight: "bold" as const },
        },
        tooltip: {
          backgroundColor: theme.tooltipBg,
          titleColor: theme.tooltipText,
          bodyColor: theme.tooltipText,
          borderColor: theme.tooltipBorder,
          borderWidth: 1,
          cornerRadius: 8,
          padding: 10,
        },
      },
    };
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="w-full h-full max-w-md max-h-80">
          <Pie data={chartData} options={options} />
        </div>
      </div>
    );
  };

  const renderScatterChart = (data: any[]) => {
    if (!data || data.length === 0) {
      return (
        <NoDataState
          type="data"
          message="No scatter data available"
          showSuggestions={false}
          showExtras={false}
        />
      );
    }

    const theme = getChartTheme();
    const headers = Object.keys(data[0]);
    const numericKeys = headers.filter((key) =>
      data.some((row) => parseNumericValue(row[key]) !== null),
    );
    if (numericKeys.length < 2) {
      return (
        <NoDataState
          type="data"
          message="At least two numeric fields are required for scatter charts"
          showSuggestions={false}
          showExtras={false}
        />
      );
    }

    const labelKey =
      headers.find((key) => !numericKeys.includes(key)) || headers[0];
    const [xKey, yKey, radiusKey] = numericKeys;
    const points = data
      .map((row) => {
        const x = parseNumericValue(row[xKey]);
        const y = parseNumericValue(row[yKey]);
        const radius = radiusKey ? parseNumericValue(row[radiusKey]) : null;
        if (x === null || y === null) {
          return null;
        }
        return {
          x,
          y,
          label: String(row[labelKey] ?? ""),
          radius: Math.max(4, Math.min(16, Math.abs(radius ?? 8) / 5)),
          rawRadius: radius,
        };
      })
      .filter(Boolean) as Array<{
      x: number;
      y: number;
      label: string;
      radius: number;
      rawRadius: number | null;
    }>;

    const chartData = {
      datasets: [
        {
          label:
            title || `${yKey.replace(/_/g, " ")} by ${xKey.replace(/_/g, " ")}`,
          data: points,
          backgroundColor: "rgba(59, 130, 246, 0.8)",
          borderColor: "rgba(37, 99, 235, 1)",
          borderWidth: 1,
          pointRadius: points.map((point) => point.radius),
          pointHoverRadius: points.map((point) => point.radius + 2),
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        title: {
          display: !!title,
          text: title || "Scatter Plot",
          color: theme.textColor,
          font: { size: 14, weight: "bold" as const },
        },
        tooltip: {
          backgroundColor: theme.tooltipBg,
          titleColor: theme.tooltipText,
          bodyColor: theme.tooltipText,
          borderColor: theme.tooltipBorder,
          borderWidth: 1,
          cornerRadius: 8,
          padding: 10,
          callbacks: {
            title: (items: any[]) => items[0]?.raw?.label || "",
            label: (item: any) => {
              const point = item.raw || {};
              const lines = [
                `${xKey}: ${formatAxisValue(Number(point.x) || 0)}`,
                `${yKey}: ${formatAxisValue(Number(point.y) || 0)}`,
              ];
              if (
                radiusKey &&
                point.rawRadius !== null &&
                point.rawRadius !== undefined
              ) {
                lines.push(
                  `${radiusKey}: ${Number(point.rawRadius).toFixed(2)}%`,
                );
              }
              return lines;
            },
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          title: { display: true, text: xKey, color: theme.textColor },
          grid: { color: theme.gridColor },
          ticks: {
            color: theme.textColor,
            callback: (value: any) => formatAxisValue(Number(value)),
          },
        },
        y: {
          beginAtZero: true,
          title: { display: true, text: yKey, color: theme.textColor },
          grid: { color: theme.gridColor },
          ticks: {
            color: theme.textColor,
            callback: (value: any) => formatAxisValue(Number(value)),
          },
        },
      },
    };

    return (
      <div className="h-full w-full">
        <Scatter data={chartData} options={options} />
      </div>
    );
  };

  const renderMapChart = (data: any[]) => {
    if (!data || data.length === 0) {
      return (
        <NoDataState
          type="data"
          message="No map locations available"
          showSuggestions={false}
          showExtras={false}
        />
      );
    }

    const points = data
      .map((row) => {
        const latitude = parseNumericValue(row.Latitude ?? row.latitude);
        const longitude = parseNumericValue(row.Longitude ?? row.longitude);
        const growth = parseNumericValue(
          row["Growth (1 Month)"] ?? row.last_1_month_invoiced_growth,
        );
        if (latitude === null || longitude === null || growth === null) {
          return null;
        }
        return {
          latitude,
          longitude,
          growth,
          branchCode: String(row["Branch Code"] ?? row.branch_code ?? ""),
        };
      })
      .filter(Boolean) as Array<{
      latitude: number;
      longitude: number;
      growth: number;
      branchCode: string;
    }>;

    if (!points.length) {
      return (
        <NoDataState
          type="data"
          message="No valid coordinates available"
          showSuggestions={false}
          showExtras={false}
        />
      );
    }

    const mapWidth = 1100;
    const mapHeight = 420;
    const baseProjected = points.map((point) =>
      projectMercator(point.latitude, point.longitude, 0),
    );
    const minX = Math.min(...baseProjected.map((point) => point.x));
    const maxX = Math.max(...baseProjected.map((point) => point.x));
    const minY = Math.min(...baseProjected.map((point) => point.y));
    const maxY = Math.max(...baseProjected.map((point) => point.y));
    const xSpan = Math.max(maxX - minX, 0.01);
    const ySpan = Math.max(maxY - minY, 0.01);
    const zoom = clampValue(
      Math.floor(
        Math.log2(Math.min((mapWidth - 120) / xSpan, (mapHeight - 80) / ySpan)),
      ),
      4,
      7,
    );
    const zoomScale = Math.pow(2, zoom);
    const centerX = ((minX + maxX) / 2) * zoomScale;
    const centerY = ((minY + maxY) / 2) * zoomScale;
    const left = centerX - mapWidth / 2;
    const top = centerY - mapHeight / 2;
    const tileStartX = Math.floor(left / 256);
    const tileEndX = Math.floor((left + mapWidth) / 256);
    const tileStartY = Math.floor(top / 256);
    const tileEndY = Math.floor((top + mapHeight) / 256);
    const tileCount = Math.pow(2, zoom);
    const tiles: Array<{
      key: string;
      x: number;
      y: number;
      urlX: number;
      urlY: number;
    }> = [];
    for (let tileX = tileStartX; tileX <= tileEndX; tileX += 1) {
      for (let tileY = tileStartY; tileY <= tileEndY; tileY += 1) {
        if (tileY < 0 || tileY >= tileCount) continue;
        const wrappedTileX = ((tileX % tileCount) + tileCount) % tileCount;
        tiles.push({
          key: `${tileX}-${tileY}`,
          x: tileX * 256 - left,
          y: tileY * 256 - top,
          urlX: wrappedTileX,
          urlY: tileY,
        });
      }
    }

    const maxGrowth = Math.max(
      ...points.map((point) => Math.abs(point.growth)),
      1,
    );

    return (
      <div className="h-full w-full overflow-hidden rounded-sm bg-[#c8d7e6]">
        <div className="relative h-full w-full">
          {tiles.map((tile) => (
            <img
              key={tile.key}
              alt=""
              className="absolute select-none"
              draggable={false}
              src={`https://tile.openstreetmap.org/${zoom}/${tile.urlX}/${tile.urlY}.png`}
              style={{
                left: `${(tile.x / mapWidth) * 100}%`,
                top: `${(tile.y / mapHeight) * 100}%`,
                width: `${(256 / mapWidth) * 100}%`,
                height: `${(256 / mapHeight) * 100}%`,
              }}
            />
          ))}
          {points.map((point) => {
            const projected = projectMercator(
              point.latitude,
              point.longitude,
              zoom,
            );
            const leftPercent = ((projected.x - left) / mapWidth) * 100;
            const topPercent = ((projected.y - top) / mapHeight) * 100;
            const diameter =
              10 + Math.sqrt(Math.abs(point.growth) / maxGrowth) * 24;
            return (
              <button
                type="button"
                key={`${point.branchCode}-${point.latitude}-${point.longitude}`}
                aria-label={`${point.branchCode || "Location"} map point`}
                className="absolute z-10 block rounded-full border border-[#006bcf] bg-[#1a8cff]/80 p-0 shadow-[0_0_0_1px_rgba(255,255,255,0.75)] outline-none focus:ring-2 focus:ring-blue-700 focus:ring-offset-1"
                onBlur={() => setMapTooltip(null)}
                onFocus={() =>
                  setMapTooltip({ ...point, leftPercent, topPercent })
                }
                onMouseEnter={() =>
                  setMapTooltip({ ...point, leftPercent, topPercent })
                }
                onMouseLeave={() => setMapTooltip(null)}
                style={{
                  left: `${leftPercent}%`,
                  top: `${topPercent}%`,
                  width: `${diameter}px`,
                  height: `${diameter}px`,
                  transform: "translate(-50%, -50%)",
                }}
              />
            );
          })}
          {mapTooltip && (
            <div
              className="pointer-events-none absolute z-20 w-80 bg-white px-4 py-3 text-xs text-neutral-800 shadow-lg"
              style={{
                left: `${clampValue(mapTooltip.leftPercent, 18, 82)}%`,
                top: `${clampValue(mapTooltip.topPercent, 18, 82)}%`,
                transform: "translate(-50%, -112%)",
              }}
            >
              <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1.5">
                <span className="text-right text-neutral-700">latitude</span>
                <span className="text-left text-neutral-900">
                  {formatMapTooltipValue(mapTooltip.latitude, 6)}
                </span>
                <span className="text-right text-neutral-700">longitude</span>
                <span className="text-left text-neutral-900">
                  {formatMapTooltipValue(mapTooltip.longitude, 6)}
                </span>
                <span className="text-right text-neutral-700">
                  Average of Last 1 Month Invoiced (Growth %)
                </span>
                <span className="text-left text-neutral-900">
                  {formatMapTooltipValue(mapTooltip.growth, 12)}
                </span>
                <span className="text-right text-neutral-700">
                  First Branch Code
                </span>
                <span className="text-left text-neutral-900">
                  {mapTooltip.branchCode || "(Blank)"}
                </span>
              </div>
            </div>
          )}
          <div className="absolute bottom-2 left-2 rounded bg-white/85 px-2 py-1 text-[10px] font-medium text-slate-600 shadow-sm">
            Map data © OpenStreetMap contributors
          </div>
        </div>
      </div>
    );
  };

  const renderTextResponse = (text: string) => (
    <div className="text-gray-800 dark:text-gray-200 p-4 whitespace-pre-wrap">
      {text}
    </div>
  );

  const renderMetricCard = (data: any[]) => {
    if (!data || data.length === 0) {
      return (
        <NoDataState
          type="data"
          message="No metrics to display"
          showSuggestions={false}
          showExtras={false}
        />
      );
    }

    let value: any;
    let label: string;
    let icon: string = "analytics";

    if (data.length === 1) {
      if (!data[0] || typeof data[0] !== "object") {
        value = "N/A";
        label = "INVALID DATA";
        icon = "error";
      } else {
        const headers = Object.keys(data[0]);
        const numericKey = headers.find(
          (k) => typeof data[0][k] === "number" || !isNaN(Number(data[0][k])),
        );
        if (numericKey) {
          value = data[0][numericKey];
          label = numericKey.replace(/_/g, " ").toUpperCase();
          // Set appropriate icon based on label
          if (label.includes("SITE")) icon = "location_on";
          else if (label.includes("USER") || label.includes("EMPLOYEE"))
            icon = "group";
          else if (label.includes("VEHICLE") || label.includes("CAR"))
            icon = "directions_car";
          else if (label.includes("REPORT")) icon = "description";
          else if (label.includes("BEAT")) icon = "business";
          else if (label.includes("CALL")) icon = "phone";
          else if (label.includes("INCIDENT")) icon = "warning";
          else icon = "trending_up";
        } else if (headers[0]) {
          value = data[0][headers[0]];
          label = headers[0].replace(/_/g, " ").toUpperCase();
          icon = "info";
        } else {
          value = "N/A";
          label = "NO DATA";
          icon = "error";
        }
      }
    } else {
      value = data.length;
      label = "TOTAL RECORDS";
      icon = "format_list_numbered";
      if (data.length > 0 && data[0] && typeof data[0] === "object") {
        const headers = Object.keys(data[0]);
        const numericKey = headers.find((k) =>
          data.every(
            (r) =>
              r &&
              typeof r === "object" &&
              (typeof r[k] === "number" || !isNaN(Number(r[k]))),
          ),
        );
        if (numericKey) {
          value = data.reduce(
            (sum, r) => sum + (Number(r && r[numericKey]) || 0),
            0,
          );
          label = `TOTAL ${numericKey.replace(/_/g, " ").toUpperCase()}`;
          // Set appropriate icon based on aggregated data
          if (label.includes("SITE")) icon = "location_on";
          else if (label.includes("USER") || label.includes("EMPLOYEE"))
            icon = "group";
          else if (label.includes("VEHICLE") || label.includes("CAR"))
            icon = "directions_car";
          else if (label.includes("REPORT")) icon = "description";
          else if (label.includes("BEAT")) icon = "business";
          else icon = "trending_up";
        }
      }
    }

    const formatValue = (val: any) => {
      const num = Number(val);
      if (isNaN(num)) return val;
      if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
      if (num >= 1000) return (num / 1000).toFixed(1) + "K";
      return num.toLocaleString();
    };

    const savedTitle = title?.trim();
    const displayLabel = savedTitle || label;

    // Determine color based on label/icon type
    let colorClass = "bg-blue-100 text-blue-600"; // default
    if (label.includes("SITE")) colorClass = "bg-blue-100 text-blue-600";
    else if (label.includes("USER") || label.includes("EMPLOYEE"))
      colorClass = "bg-orange-100 text-orange-600";
    else if (label.includes("VEHICLE") || label.includes("CAR"))
      colorClass = "bg-purple-100 text-purple-600";
    else if (label.includes("BEAT"))
      colorClass = "bg-yellow-100 text-yellow-600";
    else if (label.includes("REPORT"))
      colorClass = "bg-indigo-100 text-indigo-600";
    else if (label.includes("CALL")) colorClass = "bg-green-100 text-green-600";
    else if (label.includes("INCIDENT")) colorClass = "bg-red-100 text-red-600";

    return (
      <div className="group bg-white dark:bg-slate-800 py-4 px-3 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm flex items-center space-x-2 relative hover:shadow-md transition-shadow h-full min-h-[120px] metric-card-container">
        <div
          className={`size-10 flex items-center justify-center rounded-full ${colorClass} flex-shrink-0`}
        >
          <span className="material-symbols-outlined">{icon}</span>
        </div>
        <div className="flex-1 min-w-0 mr-0">
          <div className="relative group/tooltip inline-block max-w-full">
            <div className="text-xl font-bold text-slate-800 dark:text-slate-100 text-nowrap truncate">
              {formatValue(value)}
            </div>
            <div className="absolute bottom-full left-0 mb-2 opacity-0 group-hover/tooltip:opacity-100 transition-opacity duration-200 invisible group-hover/tooltip:visible z-50 pointer-events-none">
              <div className="bg-slate-800 dark:bg-slate-700 text-slate-100 text-xs rounded py-1 px-2.5 shadow-xl whitespace-nowrap relative border border-slate-700 dark:border-slate-600">
                {String(value)}
                <div className="absolute -bottom-[5px] left-4 w-2.5 h-2.5 bg-slate-800 dark:bg-slate-700 border-b border-r border-slate-700 dark:border-slate-600 rotate-45"></div>
              </div>
            </div>
          </div>
          <div
            className="text-xs text-slate-500 dark:text-slate-400 font-medium leading-snug line-clamp-2"
            title={displayLabel}
          >
            {displayLabel}
          </div>
          {data.length > 1 && (
            <div className="text-xs text-slate-400 mt-1">
              Based on {data.length} records
            </div>
          )}
        </div>
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 z-10">
          {/* Drag Handle */}
          {dragHandleProps && (
            <div
              {...dragHandleProps}
              className="p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 text-gray-400 hover:text-slate-600 dark:hover:text-slate-300 cursor-move drag-handle"
              title="Drag to reorder"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M11 18c0 1.1-.9 2-2 2s-2-.9-2 2 .9-2 2-2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
              </svg>
            </div>
          )}

          {onAskMore && (
            <button
              onClick={() =>
                onAskMore(
                  `Tell me more about ${displayLabel.toLowerCase()} statistics`,
                  { metric: displayLabel, value: value, id: widgetId },
                )
              }
              title={`Ask more about ${displayLabel}`}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: "16px" }}
              >
                auto_awesome
              </span>
            </button>
          )}

          {/* Kebab Menu for metric cards */}
          {(onInfo || onEdit || onExport || onDelete) && (
            <div className="relative z-50 kebab-menu-container-dv">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowKebabMenu && setShowKebabMenu(!showKebabMenu);
                }}
                className="p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 text-gray-400 hover:text-slate-600 dark:hover:text-slate-300 transition-all duration-200"
                title="More options"
              >
                <svg
                  className="h-4 w-4"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                </svg>
              </button>

              {showKebabMenu && (
                <div className="absolute right-0 top-8 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50 min-w-[120px]">
                  {onInfo && (
                    <button
                      onClick={(e) => {
                        console.log("Info button clicked in DataVisualizer!");
                        e.stopPropagation();
                        onInfo();
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center space-x-2 rounded-t-lg"
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span>Info</span>
                    </button>
                  )}
                  {onEdit && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit();
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center space-x-2"
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      <span>Edit</span>
                    </button>
                  )}
                  {onExport && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onExport();
                      }}
                      disabled={isExporting}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center space-x-2 disabled:opacity-50"
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                        />
                      </svg>
                      <span>
                        {isExporting ? "Exporting..." : exportLabel || "Export"}
                      </span>
                    </button>
                  )}
                  {onDelete && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center space-x-2 rounded-b-lg"
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                      <span>Delete</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderDoughnutChart = (data: any[]) => {
    if (!data || data.length === 0) {
      return (
        <NoDataState
          type="data"
          message="No distribution data available"
          showSuggestions={false}
          showExtras={false}
        />
      );
    }
    const theme = getChartTheme();
    const headers = Object.keys(data[0]);
    const labelKey = headers[0];
    const valueKey =
      headers.find(
        (k) => typeof data[0][k] === "number" || !isNaN(Number(data[0][k])),
      ) || headers[1];
    const chartData = {
      labels: data.map((d) => String(d[labelKey])),
      datasets: [
        {
          data: data.map((d) => Number(d[valueKey]) || 0),
          backgroundColor: CHART_COLORS.slice(0, data.length),
          borderColor: isDarkMode()
            ? "rgba(30, 41, 59, 0.8)"
            : "rgba(255, 255, 255, 0.9)",
          borderWidth: 2,
          hoverOffset: 8,
        },
      ],
    };
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      cutout: "60%",
      plugins: {
        legend: {
          position: "bottom" as const,
          labels: {
            color: theme.textColor,
            usePointStyle: true,
            padding: 12,
            font: { size: 11 },
          },
        },
        title: {
          display: !!title,
          text:
            title ||
            `${(valueKey || "").replace(/_/g, " ").toUpperCase()} Distribution`,
          color: theme.textColor,
          font: { size: 14, weight: "bold" as const },
        },
        tooltip: {
          backgroundColor: theme.tooltipBg,
          titleColor: theme.tooltipText,
          bodyColor: theme.tooltipText,
          borderColor: theme.tooltipBorder,
          borderWidth: 1,
          cornerRadius: 8,
          padding: 10,
        },
      },
    };
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="w-full h-full max-w-md max-h-80">
          <Doughnut data={chartData} options={options} />
        </div>
      </div>
    );
  };

  const renderGaugeChart = (data: any[]) => {
    if (!data || data.length === 0) {
      return (
        <NoDataState
          type="data"
          message="No gauge data available"
          showSuggestions={false}
          showExtras={false}
        />
      );
    }
    const row = data[0] || {};
    const headers = Object.keys(row);
    const numericKeys = headers.filter(
      (k) => parseNumericValue(row[k]) !== null,
    );
    if (numericKeys.length === 0) {
      return (
        <NoDataState
          type="data"
          message="No numeric gauge data available"
          showSuggestions={false}
          showExtras={false}
        />
      );
    }
    const maxKey = numericKeys.find(isGaugeMaxKey);
    const valueKey = numericKeys.find((k) => k !== maxKey) || numericKeys[0];
    const rawVal = parseNumericValue(row[valueKey]) ?? 0;
    const maxFromData = maxKey ? parseNumericValue(row[maxKey]) : null;
    const isPercentGauge = !maxFromData && isPercentageKey(valueKey);
    const maxValue = maxFromData && maxFromData > 0 ? maxFromData : 100;
    const boundedValue = Math.min(Math.max(rawVal, 0), maxValue);
    const remaining = Math.max(maxValue - boundedValue, 0);
    const progressPercent = maxValue > 0 ? (boundedValue / maxValue) * 100 : 0;
    const label = title?.trim() || valueKey.replace(/_/g, " ").toUpperCase();

    // Color based on value
    const getGaugeColor = (v: number) => {
      if (v >= 75) return "rgba(34, 197, 94, 0.95)"; // Green
      if (v >= 50) return "rgba(245, 158, 11, 0.9)"; // Amber
      if (v >= 25) return "rgba(249, 115, 22, 0.9)"; // Orange
      return "rgba(239, 68, 68, 0.85)"; // Red
    };

    const chartData = {
      labels: [label, ""],
      datasets: [
        {
          data: [boundedValue, remaining],
          backgroundColor: [
            getGaugeColor(progressPercent),
            isDarkMode() ? "rgba(51, 65, 85, 0.3)" : "rgba(229, 231, 235, 0.9)",
          ],
          borderColor: ["transparent", "transparent"],
          borderWidth: 0,
          circumference: 180,
          rotation: 270,
          borderRadius: 0,
        },
      ],
    };
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      cutout: "68%",
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
      },
    };
    return (
      <div className="h-full w-full flex flex-col justify-center px-5 py-4">
        <div
          className="relative mx-auto w-full max-w-[440px]"
          style={{ height: "220px" }}
        >
          <div className="absolute inset-x-6 top-0 bottom-12">
            <Doughnut data={chartData} options={options} />
          </div>
          <div className="absolute left-1/2 top-[58%] -translate-x-1/2 -translate-y-1/2 pointer-events-none">
            <div className="text-4xl font-light leading-none text-slate-600 dark:text-slate-200">
              {formatGaugeValue(rawVal, isPercentGauge)}
            </div>
          </div>
          <div className="absolute inset-x-2 bottom-9 flex justify-between text-sm text-slate-500 dark:text-slate-400 pointer-events-none">
            <span className="min-w-10 text-left">0</span>
            <span className="min-w-10 text-right">
              {formatGaugeValue(maxValue, isPercentGauge)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  // Apply client-side visualization correction before rendering
  const correctedVizType = correctVisualization(data, visualizationType);

  switch (correctedVizType?.toLowerCase()) {
    case "bar_chart":
    case "bar":
      return renderBarChart(data);
    case "line_chart":
    case "line":
      return renderLineChart(data);
    case "combo_chart":
      return renderComboChart(data);
    case "pie_chart":
    case "pie_chart_full":
    case "pie":
      return renderPieChart(data);
    case "scatter_chart":
    case "scatter":
      return renderScatterChart(data);
    case "map":
      return renderMapChart(data);
    case "doughnut":
    case "donut":
      return renderDoughnutChart(data);
    case "gauge":
      return renderGaugeChart(data);
    case "metric_card":
    case "metric":
      return renderMetricCard(data);
    case "table":
      return renderDataTable(data);
    case "text":
      return renderTextResponse(data[0]);
    default:
      return renderDataTable(data);
  }
};
